import { useEffect, useRef } from 'react';
import { useSocketStore } from '../../stores/useSocketStore';
import { useLinesStore } from '../../stores/useLinesStore';
import { useDateStore } from '../../stores/useDateStore';
import { usePontosItinerarioStore } from '../../stores/usePontosItinerarioStore';
import { useTurnosStore } from '../../stores/useTurnosStore';
import { useLoadingStore } from '../../stores/useLoadingStore';

import {
    loadLines,
    loadPontosByCodigo,
    finishLinesRequest,
    clearLinesRequests,
    isLinesRequestActive
} from '../../services/linesService';

import {
    loadTrips,
    clearTripsRequests
} from '../../services/tripsService';

// =========================
// DADOS POR REQUEST
// =========================

const dataByRequest = new Map();

// =========================
// REALTIME PENDENTE
// =========================

const pendingLineEvents = [];

let pendingReload = false;

export function useLinesSocket() {

    const socket = useSocketStore(state => state.socket);

    const today = useDateStore(state => state.today);

    const previousDateRef = useRef(today);

    // =========================
    // LISTENERS
    // =========================

    useEffect(() => {

        if (!socket) {
            return;
        }

        const {
            upsertLinha,
            removeLinha,
            setMetrosTotalRotaLinha,
            removeMetrosTotalRotaLinha
        } = useLinesStore.getState();

        const {
            setLoadProgress
        } = useLoadingStore.getState();

        // =========================
        // PROGRESSO
        // =========================

        const handleProgress = ({
            requestId,
            date,
            percentage
        }) => {

            const currentDate = useDateStore.getState().today;

            if (currentDate !== date) {
                return;
            }

            if (
                !isLinesRequestActive(
                    requestId,
                    date
                )
            ) {
                return;
            }

            setLoadProgress(percentage ?? 0);

        };

        // =========================
        // DATA CHUNK
        // =========================

        const handleDataChunk = ({
            requestId,
            date,
            pontos,
            metrosTotalRota,
            percentage
        }) => {

            const currentDate =
                useDateStore
                    .getState()
                    .today;

            if (currentDate !== date) {
                return;
            }

            if (!isLinesRequestActive(requestId, date)) {
                return;
            }

            let accumulated =
                dataByRequest.get(
                    requestId
                );

            if (!accumulated) {

                accumulated = {
                    pontos: {},
                    metrosTotalRota: {}
                };

                dataByRequest.set(
                    requestId,
                    accumulated
                );

            }

            Object.assign(
                accumulated.pontos,
                pontos || {}
            );

            Object.assign(
                accumulated.metrosTotalRota,
                metrosTotalRota || {}
            );

            setLoadProgress(percentage ?? 0);

        };

        // =========================
        // DONE
        // =========================

        const handleDone = async ({
            requestId,
            date,
            linhas,
            turnos,
            turnosCodes
        }) => {

            const currentDate = useDateStore.getState().today;

            const isActive =
                isLinesRequestActive(
                    requestId,
                    date
                );

            if (
                !isActive ||
                currentDate !== date
            ) {

                dataByRequest.delete(requestId);

                finishLinesRequest(requestId);

                return;
            }

            // =========================
            // INSTALA LINHAS
            // =========================

            useLinesStore
                .getState()
                .setLinhas(
                    linhas || []
                );

            // =========================
            // INSTALA TURNOS
            // =========================

            const {
                setTurnos,
                setTurnosCodes
            } = useTurnosStore.getState();

            if (Array.isArray(turnos)) {
                setTurnos(turnos, false);
            }

            if (Array.isArray(turnosCodes)) {
                setTurnosCodes(turnosCodes, false);
            }
            
            // =========================
            // RECUPERA DADOS
            // =========================

            const data =
                dataByRequest.get(
                    requestId
                ) || {};

            dataByRequest.delete(requestId);

            const pontos = data.pontos || {};

            const metrosTotalRota = data.metrosTotalRota || {};

            // =========================
            // INSTALA METROS TOTAL ROTA
            // =========================

            useLinesStore
                .getState()
                .setMetrosTotalRota(
                    metrosTotalRota
                );

            // =========================
            // INSTALA PONTOS
            // =========================

            usePontosItinerarioStore
                .getState()
                .setPontosBatch(
                    pontos
                );

            // =========================
            // FINALIZA REQUEST
            // =========================

            setLoadProgress(100);

            finishLinesRequest(requestId);

            // =========================
            // AINDA EXISTEM LINHAS
            // SENDO CARREGADAS
            // =========================

            const {
                loadingLines
            } = useLoadingStore.getState();

            if (loadingLines) {
                return;
            }

            // =========================
            // APLICA REALTIME PENDENTE
            // =========================

            await flushPendingEvents();

            // =========================
            // RELOAD PENDENTE
            // =========================

            if (pendingReload) {

                pendingReload = false;

                loadLines(socket);

                return;
            }

            // =========================
            // INICIA TRIPS
            // =========================

            loadTrips(socket);

        };

        // =========================
        // ERROR
        // =========================

        const handleError = ({
            requestId,
            date
        }) => {

            const currentDate = useDateStore.getState().today;

            const isActive =
                isLinesRequestActive(
                    requestId,
                    date
                );

            dataByRequest.delete(requestId);

            finishLinesRequest(requestId);

            if (
                !isActive ||
                currentDate !== date
            ) {
                return;
            }

            console.error('Erro ao carregar linhas');

            setLoadProgress(100);

        };

        // =========================
        // APLICA EVENTO REALTIME
        // =========================

        const applyRealtimeEvent = async ({
            type,
            payload
        }) => {

            switch (type) {

                case 'upsert': {

                    upsertLinha(payload);

                    const codigo = String(payload?.CODIGO_INTEGRACAO || '').trim();

                    if (!codigo) {
                        break;
                    }

                    const {codigosComPontos} =
                        usePontosItinerarioStore
                            .getState();

                    if (codigosComPontos.has(codigo)) {
                        break;
                    }

                    const result =
                        await loadPontosByCodigo(
                            socket,
                            codigo
                        );

                    if (!result) {
                        break;
                    }

                    usePontosItinerarioStore
                        .getState()
                        .setPontosBatch({
                            [result.codigo]:
                                result.pontos
                        });

                    break;
                }

                case 'delete': {

                    removeLinha(payload);

                    usePontosItinerarioStore
                        .getState()
                        .deletePontosByCodigo(payload);

                    break;
                }

                case 'metros-total-rota': {

                    setMetrosTotalRotaLinha(
                        payload?.codigo,
                        payload?.metrosTotalRota
                    );

                    break;
                }

                case 'metros-total-rota-delete': {

                    removeMetrosTotalRotaLinha(
                        payload?.codigo
                    );

                    break;
                }

                default:
                    break;

            }

        };

        // =========================
        // RECEBE EVENTO REALTIME
        // =========================

        const handleRealtimeEvent = async (
            type,
            payload
        ) => {

            const {
                loadingLines
            } = useLoadingStore.getState();

            if (loadingLines) {

                pendingLineEvents.push({
                    type,
                    payload
                });

                return;
            }

            await applyRealtimeEvent({
                type,
                payload
            });

        };

        // =========================
        // FLUSH REALTIME
        // =========================

        const flushPendingEvents = async () => {

            if (!pendingLineEvents.length) {
                return;
            }

            const events =
                pendingLineEvents.splice(
                    0,
                    pendingLineEvents.length
                );

            for (const event of events) {

                await applyRealtimeEvent(event);

            }

        };

        // =========================
        // RELOAD
        // =========================

        const handleReload = () => {

            const {
                loadingLines
            } = useLoadingStore.getState();

            if (loadingLines) {

                pendingReload = true;

                return;
            }

            loadLines(socket);

        };

        // =========================
        // REALTIME
        // =========================

        const handleSync = () => {

            handleReload();

        };

        const handleUpsert = linha => {

            handleRealtimeEvent('upsert', linha);

        };

        const handleDelete = codigo => {

            handleRealtimeEvent('delete', codigo);

        };

        const handleMetrosTotalRotaUpdated = payload => {

            handleRealtimeEvent(
                'metros-total-rota',
                payload
            );

        };

        const handleMetrosTotalRotaDeleted = payload => {

            handleRealtimeEvent(
                'metros-total-rota-delete',
                payload
            );

        };

        const handleInvalidate = () => {

            handleReload();

        };

        const handleDisconnect = () => {

            dataByRequest.clear();

            pendingLineEvents.length = 0;

            pendingReload = false;

            clearLinesRequests();
        };

        // =========================
        // LISTENERS
        // =========================

        socket.on('disconnect', handleDisconnect);

        socket.on('linhas:progress', handleProgress);

        socket.on('linhas:data-chunk', handleDataChunk);

        socket.on('linhas:done', handleDone);

        socket.on('linhas:error', handleError);

        socket.on('linhas:sync', handleSync);

        socket.on( 'linhas:upserted', handleUpsert);

        socket.on('linhas:deleted', handleDelete);

        socket.on('metros-total-rota:updated', handleMetrosTotalRotaUpdated);

        socket.on('metros-total-rota:deleted', handleMetrosTotalRotaDeleted);

        socket.on('linhas:invalidate', handleInvalidate);

        return () => {

            socket.off('disconnect', handleDisconnect);

            socket.off('linhas:progress', handleProgress);

            socket.off('linhas:data-chunk', handleDataChunk);

            socket.off('linhas:done', handleDone);

            socket.off('linhas:error', handleError);

            socket.off('linhas:sync', handleSync);

            socket.off('linhas:upserted', handleUpsert);

            socket.off('linhas:deleted', handleDelete);

            socket.off('metros-total-rota:updated', handleMetrosTotalRotaUpdated);

            socket.off('metros-total-rota:deleted', handleMetrosTotalRotaDeleted);

            socket.off('linhas:invalidate', handleInvalidate);

            pendingLineEvents.length = 0;

            pendingReload = false;

        };

    }, [socket]);

    // =========================
    // MUDANÇA DE DATA
    // =========================

    useEffect(() => {

        const previousDate = previousDateRef.current;

        previousDateRef.current = today;

        // =========================
        // NÃO MUDOU A DATA
        // =========================

        if (
            !previousDate ||
            previousDate === today
        ) {
            return;
        }

        // =========================
        // SOCKET
        // =========================

        if (!socket?.connected) {
            return;
        }

        // =========================
        // REINICIA LINHAS → TRIPS
        // =========================

        clearTripsRequests();

        loadLines(socket);

    }, [
        socket,
        today
    ]);

}